import { Language } from 'providers/translates/enums';
import { isVac } from './is-vac';

/** First-paint and post-Strapi English chrome for Saluting Service. */
export const VAC_EN_COPY = {
  header: {
    searchQuilts: 'Search Memorials',
    createQuilt: 'Create a Memorial',
    byline: 'Honouring duty and service to country',
    about: 'About',
    contact: 'Contact Us',
    howToUse: 'How to Use',
    resources: 'Resources',
    signIn: 'Sign In',
    logout: 'Logout',
    myAccount: 'My Account',
  },
  home: {
    title:
      'From a grateful nation, we recognize the contributions of those who dedicated their lives to service.',
    createQuiltText: 'Create a Memorial',
    searchQuiltText: 'Search Memorials',
    resourcesText: 'Find grief support',
  },
  footer: {
    contactUs: 'Contact us',
    privacyNotice: 'Privacy Notice',
    privacyPolicy: 'Privacy Policy',
    donateToday: 'Donate today',
    copyright: 'Copyright 2025 Canadian Virtual Hospice. All rights reserved.',
  },
} as const;

/** First-paint and post-Strapi French chrome for Saluons leur service. */
export const VAC_FR_COPY = {
  header: {
    searchQuilts: 'Recherche d’hommages',
    createQuilt: 'Créer un hommage',
    byline: 'Hommage pour service rendu à la patrie',
    about: 'À propos',
    contact: 'Nous joindre',
    howToUse: 'Comment procéder',
    resources: 'Ressources',
    signIn: 'Connexion',
    logout: 'Déconnexion',
    myAccount: 'Mon compte',
  },
  home: {
    title: 'Aux personnes qui lui ont consacré leur vie, la patrie reconnaissante.',
    createQuiltText: 'Créer un hommage',
    searchQuiltText: 'Recherche d’hommages',
    resourcesText: 'Trouver des services d’accompagnement du deuil',
  },
  footer: {
    contactUs: 'Nous joindre',
    privacyNotice: 'Avis de confidentialité',
    privacyPolicy: 'Politique de confidentialité',
    donateToday: 'Faire un don aujourd’hui',
    copyright: '© Portail palliatif canadien, 2025. Tous droits réservés.',
  },
} as const;

type VacChrome = {
  header: Record<string, string>;
  home: Record<string, string>;
  footer: Record<string, string>;
};

const copyForLang = (lang: string): VacChrome =>
  lang === Language.FRENCH ? VAC_FR_COPY : VAC_EN_COPY;

export const vacHeaderText = (lang: string, key: keyof typeof VAC_EN_COPY.header) =>
  copyForLang(lang).header[key];

export const vacHomeText = (lang: string, key: keyof typeof VAC_EN_COPY.home) =>
  copyForLang(lang).home[key];

export const vacFooterText = (lang: string, key: keyof typeof VAC_EN_COPY.footer) =>
  copyForLang(lang).footer[key];

export const vacHeaderLabel = (
  lang: string,
  value: string | undefined,
  key: keyof typeof VAC_EN_COPY.header,
  nonVac: string
) => value || (isVac() ? vacHeaderText(lang, key) : nonVac);

export const vacHomeLabel = (
  lang: string,
  value: string | undefined,
  key: keyof typeof VAC_EN_COPY.home,
  nonVac: string
) => value || (isVac() ? vacHomeText(lang, key) : nonVac);

export const vacFooterLabel = (
  lang: string,
  value: string | undefined,
  key: keyof typeof VAC_EN_COPY.footer,
  nonVac: string
) => value || (isVac() ? vacFooterText(lang, key) : nonVac);

export const vacSeed = () => ({
  [Language.ENGLISH]: {
    header: { ...VAC_EN_COPY.header },
    home: { ...VAC_EN_COPY.home },
    footer: { ...VAC_EN_COPY.footer },
  },
  [Language.FRENCH]: {
    header: { ...VAC_FR_COPY.header },
    home: { ...VAC_FR_COPY.home },
    footer: { ...VAC_FR_COPY.footer },
  },
});

/** @deprecated Use vacSeed. */
export const vacEnglishSeed = vacSeed;

const pinLang = (existing: Record<string, any> | undefined, pinned: VacChrome) => {
  const src = existing || {};
  return {
    ...src,
    header: { ...src.header, ...pinned.header },
    home: { ...src.home, ...pinned.home },
    footer: { ...src.footer, ...pinned.footer },
  };
};

export type TranslatesByLang = Record<string, any>;

/** Keep Saluting Service chrome when Strapi still has Healing Quilt / old VAC strings. */
export const applyVacEnglishCopy = (byLang: TranslatesByLang | undefined): TranslatesByLang => {
  if (!byLang) {
    return {};
  }
  if (!isVac()) {
    return byLang;
  }

  return {
    ...byLang,
    [Language.ENGLISH]: pinLang(byLang[Language.ENGLISH], VAC_EN_COPY),
    [Language.FRENCH]: pinLang(byLang[Language.FRENCH], VAC_FR_COPY),
  };
};
